import CoverHero from "@/assets/img/compatibility/cover-hero.png"
import IconClock from "@/assets/img/compatibility/cover/icon-clock.svg"
import IconCompass from "@/assets/img/compatibility/cover/icon-compass.svg"
import IconLines from "@/assets/img/compatibility/cover/icon-lines.svg"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { AppRoutes } from "@/shared/lib/routes"
import { LegalLink } from "@/shared/components/legal-link"
import { usePreloadQuizImages } from "../../hooks/use-preload-quiz-images"
import type { StepComponentProps } from "./step-contract"

/** Cover feature-chip icons, in design order (3-minute quiz / insights / guide). */
const CHIP_ICONS = [IconClock, IconLines, IconCompass]

/**
 * Compatibility cover (Figma LPR_Quiz_Compatibility_Step0): a full-width hero at
 * the top, then the title, the dark-surface card wrapping the feature chips and
 * the CTA, and the policy links — all grouped together at the bottom.
 */
export function CompatibilityCoverStep({ step, onAdvance }: StepComponentProps) {
  const features = step.features ?? []

  // Warm the later steps' images while the user is on this first screen, so the
  // quiz doesn't flash-load each image on step entry.
  usePreloadQuizImages("compatibility")

  return (
    <div className="flex flex-1 flex-col bg-dark-base text-purple-light">
      <img src={CoverHero} alt="" className="w-full object-cover" />

      <div className="flex flex-1 flex-col px-4 pb-4">
        <div className="mt-auto flex flex-col gap-5">
          <div className="space-y-2 text-center">
            <h1 className="font-display text-2xl font-medium text-purple-light">{step.title}</h1>
            {step.subtitle && <p className="text-base text-purple-light/70">{step.subtitle}</p>}
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2 rounded-[24px] bg-dark-surface px-1 pt-1 pb-2">
              <div className="flex gap-1">
                {features.map((feature, i) => {
                  // Split the two-word label across two lines, per the design.
                  const parts = feature.label.split(" ")
                  const head = parts.slice(0, -1).join(" ")
                  const tail = parts[parts.length - 1]
                  return (
                    <div
                      key={feature.label}
                      className="flex flex-1 flex-col items-center justify-end gap-2 rounded-[20px] bg-purple-light/10 px-2 py-3"
                    >
                      <img
                        src={CHIP_ICONS[i] ?? IconClock}
                        alt=""
                        className={cn("size-6", feature.icon === "lines" && "-rotate-90")}
                      />
                      <span className="text-center text-sm leading-tight text-purple-light">
                        {head && (
                          <>
                            {head}
                            <br />
                          </>
                        )}
                        {tail}
                      </span>
                    </div>
                  )
                })}
              </div>
              <div className="px-1">
                <Button variant="gradient" size="xl" className="w-full" onClick={onAdvance}>
                  {step.cta ?? "Continue"}
                </Button>
              </div>
            </div>

            <div className="flex justify-between px-1 text-center text-xs text-purple-light/40">
              <span className="flex-1">
                <LegalLink to={AppRoutes.PRIVACY}>Privacy Policy</LegalLink>
              </span>
              <span className="flex-1">
                <LegalLink to={AppRoutes.TERMS}>Terms of use</LegalLink>
              </span>
              <span className="flex-1">
                <LegalLink to={AppRoutes.COOKIES}>Cookie Policy</LegalLink>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
